import { useCallback, useMemo } from 'react';

import { useSearchPageData } from '#src/app/pages/searchV2/pageContext';
import {
	SEARCH_CACHE_SECONDS,
	SEARCH_LOCATION_CITY_FILTER,
	SEARCH_LOCATION_FILTER_MAX_ITEMS,
} from '#src/app/pages/searchV2/shared/consts';
import type { LocationItem } from '#src/app/pages/searchV2/shared/types';

import { useSearchClient } from '../../../../../useSearchClient';
import { useSearchIndexName } from '../../../../../useSearchIndexName';

type Result = ModelHookResult<
	undefined,
	{
		searchForRelevantCities: (selectedLocation: string) => Promise<readonly LocationItem[]>;
	}
>;

export function useSearchForRelevantCities(): Result {
	const {
		data: { typesenseClient },
	} = useSearchClient();

	const {
		data: { searchIndexName },
	} = useSearchIndexName();

	const {
		data: { countries },
	} = useSearchPageData();

	const countryDisplayNameMap = useMemo(
		() => Object.fromEntries(countries.map((c) => [c.countryCode, c.displayName])),
		[countries],
	);

	return {
		actions: {
			searchForRelevantCities: useCallback(
				async (selectedLocation: string) => {
					const response = await typesenseClient
						.collections(searchIndexName)
						.documents()
						.search(
							{
								q: '*',
								query_by: '*',
								facet_by: SEARCH_LOCATION_CITY_FILTER,
								facet_query: `${SEARCH_LOCATION_CITY_FILTER}:`,
								max_facet_values: SEARCH_LOCATION_FILTER_MAX_ITEMS,
							},
							{
								cacheSearchResultsForSeconds: SEARCH_CACHE_SECONDS,
							},
						);
					if (!response.facet_counts?.length) return [];

					const facetCount = response.facet_counts.find((facet) => facet.field_name === SEARCH_LOCATION_CITY_FILTER);

					if (!facetCount?.counts) return [];

					return facetCount.counts.map(({ count, highlighted, value }) => ({
						label: countryDisplayNameMap[value] || value,
						value,
						highlighted,
						count,
						isRefined: selectedLocation === value,
						type: 'city' as const,
					}));
				},
				[countryDisplayNameMap, searchIndexName, typesenseClient],
			),
		},
	};
}
